import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Mail, Zap, Shield, Users, Bot, ArrowRight, Check, X,
  Sparkles, Clock, Send, MessageSquare, Paperclip, RefreshCw,
  Briefcase, Code, ShoppingCart, Eye, ChevronDown, ChevronUp,
  Star, Globe, Bell, TrendingUp, DollarSign, Target,
  Megaphone, KeyRound, Heart, BarChart3, ArrowDown, Lock, RotateCcw
} from 'lucide-react';
import { CompanyTrust, TrustBadges, LiveStats, SystemStatus, WhyWeCharge, FounderStory, Testimonials, TrustBanner } from '../components/TrustSections';
import SeeItInAction from '../components/SeeItInAction';
import { blogPosts } from '../data/blogData';

const BOT_LINK = "https://t.me/instant_email_bot";
const CHANNEL_LINK = "https://t.me/freetelegramemail";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_staymii-telegram-bot/artifacts/u3ivfsly_A9358B2D-0233-4058-88E9-3B869B36D815.png";
const QR_CODE_URL = "/qr-telegram-bot.png";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.08 } }
};

function trackCTAClick(action) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, { event_category: 'CTA', event_label: 'cta_click' });
  }
}

// ─── TELEGRAM SVG ICON ───
function TelegramIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
    </svg>
  );
}

// ─── HEADER ───
function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { label: "Use Cases", href: "/use-cases" },
    { label: "Guides", href: "/telegram-email-bot" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "Pricing", href: "#pricing" },
    { label: "FAQ", href: "#faq" },
    { label: "Blog", href: "/blog" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/80 backdrop-blur-xl border-b border-gray-200/40 shadow-sm' : 'bg-transparent'
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <a href="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src={LOGO_URL} alt="Instant Email Bot" className="w-7 h-7 rounded-md object-cover" />
            <span className="font-outfit font-bold text-lg text-gray-900">TelegramEmailBot</span>
          </a>
          <nav className="hidden md:flex items-center gap-6">
            {navLinks.map(link => (
              link.href.startsWith('/') ? (
                <Link key={link.label} to={link.href} className="font-manrope text-sm text-gray-600 hover:text-gray-900 transition-colors">{link.label}</Link>
              ) : (
                <a key={link.label} href={link.href} className="font-manrope text-sm text-gray-600 hover:text-gray-900 transition-colors">{link.label}</a>
              )
            ))}
            <SystemStatus />
            <a
              href={BOT_LINK}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackCTAClick('header_cta')}
              className="font-manrope text-sm font-semibold bg-[#0088CC] text-white px-5 py-2 rounded-full hover:bg-[#006699] transition-all hover:-translate-y-0.5 shadow-md"
              data-testid="header-cta-button"
            >
              Start Now
            </a>
          </nav>
          <button className="md:hidden p-2" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-toggle">
            <div className="w-5 h-0.5 bg-gray-900 mb-1" />
            <div className="w-5 h-0.5 bg-gray-900 mb-1" />
            <div className="w-5 h-0.5 bg-gray-900" />
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden pb-4 border-t border-gray-100">
            {navLinks.map(link => (
              link.href.startsWith('/') ? (
                <Link key={link.label} to={link.href} className="block py-2 font-manrope text-sm text-gray-600" onClick={() => setMenuOpen(false)}>{link.label}</Link>
              ) : (
                <a key={link.label} href={link.href} className="block py-2 font-manrope text-sm text-gray-600" onClick={() => setMenuOpen(false)}>{link.label}</a>
              )
            ))}
            <a href={BOT_LINK} target="_blank" rel="noopener noreferrer" className="block mt-2 text-center font-manrope text-sm font-semibold bg-[#0088CC] text-white px-5 py-2 rounded-full">
              Start Now
            </a>
          </div>
        )}
      </div>
    </header>
  );
}

// ─── HERO ───
function Hero() {
  return (
    <section className="relative pt-28 pb-16 sm:pt-36 sm:pb-24 px-4 overflow-hidden" data-testid="hero-section">
      {/* Subtle grid background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,136,204,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,136,204,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-[radial-gradient(ellipse_at_center,rgba(0,136,204,0.06),transparent_70%)]" />

      <div className="max-w-7xl mx-auto relative">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Left copy */}
          <motion.div initial="hidden" animate="visible" variants={stagger} className="order-1">
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-[#0088CC]/10 text-[#0088CC] font-manrope font-semibold text-xs tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              <Zap className="w-3.5 h-3.5" /> No Signup Required
            </motion.div>
            <motion.h1 variants={fadeUp} className="font-outfit font-black text-4xl sm:text-5xl lg:text-[3.5rem] tracking-tight text-gray-900 leading-[1.08] mb-6">
              Your Private Email Inbox Inside Telegram
              <span className="text-[#0088CC]"> — Now with Team Collaboration</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="font-manrope text-base sm:text-lg text-gray-500 max-w-lg mb-8 leading-relaxed">
              Receive emails instantly in Telegram or your team group. Never miss a lead. No signup. Get your permanent inbox for just $4.99 one-time.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
              <a
                href={BOT_LINK}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackCTAClick('hero_start_now')}
                className="inline-flex items-center gap-2.5 bg-[#0088CC] text-white font-manrope font-bold px-8 py-3.5 rounded-full hover:bg-[#006699] transition-all hover:-translate-y-0.5 shadow-lg hover:shadow-xl"
                data-testid="hero-cta-start"
              >
                <TelegramIcon className="w-5 h-5" /> Start Now on Telegram
              </a>
              <a
                href="#pricing"
                onClick={() => trackCTAClick('hero_see_pricing')}
                className="inline-flex items-center gap-2 border border-gray-300 text-gray-700 font-manrope font-semibold px-8 py-3.5 rounded-full hover:border-gray-400 hover:bg-gray-50 transition-all"
                data-testid="hero-cta-email"
              >
                See Pricing <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>
            <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-8 text-sm text-gray-400 font-manrope">
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> No registration</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> $4.99 one-time</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> Works in seconds</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> 7-day money-back guarantee</span>
            </motion.div>

            {/* Trust strip — security & social proof */}
            <motion.div variants={fadeUp} className="mt-8" data-testid="hero-trust-strip">
              <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
                <span className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 font-manrope font-semibold text-xs px-3 py-1.5 rounded-full shadow-sm">
                  <Shield className="w-3.5 h-3.5" strokeWidth={2.5} />
                  256-bit SSL
                </span>
                <span className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-blue-700 font-manrope font-semibold text-xs px-3 py-1.5 rounded-full shadow-sm">
                  <Lock className="w-3.5 h-3.5" strokeWidth={2.5} />
                  No passwords stored
                </span>
                <span className="inline-flex items-center gap-1.5 bg-violet-50 border border-violet-200 text-violet-700 font-manrope font-semibold text-xs px-3 py-1.5 rounded-full shadow-sm">
                  <Globe className="w-3.5 h-3.5" strokeWidth={2.5} />
                  GDPR-compliant
                </span>
                <span className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-700 font-manrope font-semibold text-xs px-3 py-1.5 rounded-full shadow-sm">
                  <Users className="w-3.5 h-3.5" strokeWidth={2.5} />
                  1,200+ businesses
                </span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right — mascot image */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }} className="flex justify-center order-2">
            <img
              src="https://customer-assets.emergentagent.com/job_staymii-telegram-bot/artifacts/dbwmuy0k_A9358B2D-0233-4058-88E9-3B869B36D815.png"
              alt="Instant Email Bot mascot — friendly robot holding an email envelope"
              className="w-[75%] max-w-[360px] lg:w-full lg:max-w-md rounded-3xl drop-shadow-2xl object-contain"
              loading="eager"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ─── WHAT IS ───
function WhatIs() {
  return (
    <section className="py-20 sm:py-28 px-4 bg-[#F8F9FA]" data-testid="what-is-section">
      <div className="max-w-4xl mx-auto text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.p variants={fadeUp} className="font-manrope text-sm uppercase tracking-[0.2em] font-semibold text-gray-400 mb-4">What Is TelegramEmailBot</motion.p>
          <motion.h2 variants={fadeUp} className="font-outfit font-bold text-3xl sm:text-4xl tracking-tight text-gray-900 mb-6 leading-tight">
            TelegramEmailBot turns Telegram into a powerful email system.
          </motion.h2>
          <motion.p variants={fadeUp} className="font-manrope text-base sm:text-lg text-gray-500 leading-relaxed max-w-2xl mx-auto">
            Receive emails instantly, route them into Telegram groups, collaborate with your team, and never miss important messages again. It's email reimagined for the way teams actually work.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

// ─── 8 USE CASES ───
function UseCases() {
  const cases = [
    { icon: <Briefcase className="w-6 h-6" />, title: "Business & Work Automation", desc: "Receive booking emails, inquiries, invoices, and alerts instantly in Telegram or your team group.", example: "Booking inquiry \u2192 appears in Telegram \u2192 reply immediately.", span: "md:col-span-2" },
    { icon: <Target className="w-6 h-6" />, title: "Lead Generation Tool", desc: "Capture leads and push them into Telegram group for instant follow-up.", quote: "No more unattended leads.", span: "" },
    { icon: <Shield className="w-6 h-6" />, title: "Privacy / Burner Email", desc: "Use temporary email to avoid spam and protect your real inbox.", span: "" },
    { icon: <Sparkles className="w-6 h-6" />, title: "AI Email Assistant", desc: "Summarize long emails in one sentence, extract OTPs and key info automatically.", tag: "Live", span: "" },
    { icon: <ShoppingCart className="w-6 h-6" />, title: "E-Commerce Notifications", desc: "Track orders, customer emails, and shipping updates in real time.", span: "" },
    { icon: <Users className="w-6 h-6" />, title: "Team Support & Centralized Communication", desc: "All emails from different sources go into ONE Telegram group.", bullets: ["Entire support team inside one group", "No missed or unattended requests", "Faster response time"], quote: "Turn your Telegram group into a live support and sales command center.", coreFeature: true, span: "md:col-span-2" },
    { icon: <Megaphone className="w-6 h-6" />, title: "Marketing & Growth Tool", desc: "Track campaigns, affiliate leads, and responses instantly.", span: "" },
    { icon: <KeyRound className="w-6 h-6" />, title: "OTP / Verification Hub", desc: "Receive OTP and verification emails instantly in Telegram.", span: "" },
  ];

  return (
    <section id="use-cases" className="py-20 sm:py-28 px-4" data-testid="use-cases-section">
      <div className="max-w-7xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
          <motion.p variants={fadeUp} className="font-manrope text-sm uppercase tracking-[0.2em] font-semibold text-gray-400 mb-3">Use Cases</motion.p>
          <motion.h2 variants={fadeUp} className="font-outfit font-bold text-3xl sm:text-4xl tracking-tight text-gray-900 mb-4">8 High-Value Ways to Use TelegramEmailBot</motion.h2>
          <motion.p variants={fadeUp} className="font-manrope text-base text-gray-500 max-w-lg mx-auto">From lead capture to team support — built for real businesses.</motion.p>
        </motion.div>
        {/* Use cases infographic */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-14">
          <img
            src="https://customer-assets.emergentagent.com/job_staymii-telegram-bot/artifacts/w4cu63ax_47684FDE-8BFD-4A46-9185-FC2AFACD99DF.png"
            alt="8 use cases: Business Alerts, Lead Generation, Privacy, E-Commerce, Team Inbox, Marketing, OTP Codes"
            className="w-full max-w-4xl mx-auto rounded-2xl shadow-lg"
            loading="lazy"
            data-testid="use-cases-infographic"
          />
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
          className="grid grid-cols-1 md:grid-cols-4 gap-5"
        >
          {cases.map((c, i) => (
            <motion.div key={i} variants={fadeUp}
              className={`bg-white border ${c.coreFeature ? 'border-[#0088CC]/30 ring-1 ring-[#0088CC]/10' : 'border-gray-200'} rounded-2xl p-7 hover:shadow-md hover:border-gray-300 transition-all relative ${c.span}`}
              data-testid={`use-case-${i}`}
            >
              {c.tag && (
                <span className={`absolute top-4 right-4 font-manrope text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${c.tag === "Live" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  {c.tag === "Live" && <span className="inline-block w-1.5 h-1.5 bg-emerald-500 rounded-full mr-1 animate-pulse" aria-hidden="true" />}
                  {c.tag}
                </span>
              )}
              {c.coreFeature && (
                <span className="absolute top-4 right-4 bg-[#0088CC]/10 text-[#0088CC] font-manrope text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">Core Feature</span>
              )}
              <div className="text-[#0088CC] mb-4">{c.icon}</div>
              <h3 className="font-outfit font-bold text-lg text-gray-900 mb-2">{c.title}</h3>
              <p className="font-manrope text-sm text-gray-500 leading-relaxed">{c.desc}</p>
              {c.example && (
                <p className="font-manrope text-xs text-[#0088CC] mt-3 bg-[#0088CC]/5 rounded-lg px-3 py-2">{c.example}</p>
              )}
              {c.bullets && (
                <ul className="mt-3 space-y-1.5">
                  {c.bullets.map((b, j) => (
                    <li key={j} className="flex items-center gap-2 font-manrope text-sm text-gray-600">
                      <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" /> {b}
                    </li>
                  ))}
                </ul>
              )}
              {c.quote && (
                <p className="font-manrope text-sm font-semibold text-gray-700 mt-3 italic">"{c.quote}"</p>
              )}
            </motion.div>
          ))}
        </motion.div>
        <div className="text-center mt-12">
          <Link to="/use-cases" className="inline-flex items-center gap-2 font-manrope font-semibold text-sm text-[#0088CC] hover:text-[#006699] transition-colors" data-testid="use-cases-view-all">
            View All 30+ Use Cases <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ─── HOW IT WORKS ───
function HowItWorks() {
  const steps = [
    { num: "01", icon: <Bot className="w-8 h-8" />, title: "Start the Bot", desc: "Search @instant_email_bot in Telegram or click the link. Press Start." },
    { num: "02", icon: <Mail className="w-8 h-8" />, title: "Get Your Email Address", desc: "Pay a one-time $4.99 and your unique @telegramemailbot.com address is revealed instantly — no signup, no account." },
    { num: "03", icon: <Users className="w-8 h-8" />, title: "Receive in Telegram or Group", desc: "Every email appears as a Telegram message — in private chat or your team group." },
  ];
  return (
    <section id="how-it-works" className="py-20 sm:py-28 px-4 bg-[#F8F9FA]" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
          <motion.p variants={fadeUp} className="font-manrope text-sm uppercase tracking-[0.2em] font-semibold text-gray-400 mb-3">How It Works</motion.p>
          <motion.h2 variants={fadeUp} className="font-outfit font-bold text-3xl sm:text-4xl tracking-tight text-gray-900 mb-4">Three Steps. Five Seconds. Done.</motion.h2>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-3 gap-8">
          {steps.map((s, i) => (
            <motion.div key={i} variants={fadeUp} className="relative text-center" data-testid={`step-${i}`}>
              <div className="font-outfit font-black text-7xl text-gray-100 absolute -top-4 left-1/2 -translate-x-1/2 select-none">{s.num}</div>
              <div className="relative z-10 bg-[#0088CC]/10 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 text-[#0088CC]">{s.icon}</div>
              <h3 className="font-outfit font-bold text-xl text-gray-900 mb-3">{s.title}</h3>
              <p className="font-manrope text-sm text-gray-500 max-w-xs mx-auto">{s.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* YouTube Shorts video demo — 9:16 vertical */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-16 flex flex-col items-center"
        >
          <div className="inline-flex items-center gap-2 bg-white border border-gray-200 shadow-sm rounded-full px-4 py-1.5 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
            <span className="font-manrope text-xs font-bold text-gray-700 tracking-wide">WATCH · 60-SECOND SETUP</span>
          </div>
          <h3 className="font-outfit font-bold text-2xl sm:text-3xl text-gray-900 text-center mb-2 tracking-tight">See the full setup in 60 seconds.</h3>
          <p className="font-manrope text-sm text-gray-500 text-center mb-8 max-w-md">From <code className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-700 text-xs">/start</code> to a working email inbox in Telegram — real screen recording, no cuts.</p>

          {/* Phone-frame */}
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-[#0088CC]/20 via-transparent to-violet-400/20 rounded-[3rem] blur-2xl" aria-hidden="true" />
            <div className="relative w-[280px] sm:w-[320px] aspect-[9/16] bg-black rounded-[2.5rem] p-2 shadow-[0_30px_80px_-24px_rgba(0,136,204,0.4)] border border-gray-200">
              <div className="w-full h-full rounded-[2rem] overflow-hidden bg-black">
                <iframe
                  src="https://www.youtube.com/embed/72MufjAB5VM?rel=0&modestbranding=1&playsinline=1"
                  title="TelegramEmailBot setup — 60 second demo"
                  loading="lazy"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="w-full h-full"
                  data-testid="youtube-demo-video"
                ></iframe>
              </div>
            </div>
          </div>

          <a
            href="https://youtube.com/shorts/72MufjAB5VM"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 font-manrope font-semibold text-sm text-[#0088CC] hover:text-[#006699] transition-colors mt-6"
            data-testid="youtube-demo-link"
          >
            Watch on YouTube <ArrowRight className="w-4 h-4" />
          </a>

          {/* VideoObject structured data — lets Google index the embedded demo video
              and resolves the GSC "Video isn't on a watch page" report. */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "VideoObject",
                name: "TelegramEmailBot setup — 60 second demo",
                description:
                  "See how to get a working email address inside Telegram in 60 seconds — from /start to a live inbox, shown with a real screen recording.",
                thumbnailUrl: [
                  "https://i.ytimg.com/vi/72MufjAB5VM/maxresdefault.jpg",
                  "https://i.ytimg.com/vi/72MufjAB5VM/hqdefault.jpg",
                ],
                uploadDate: "2026-07-09T09:00:00+08:00",
                contentUrl: "https://www.youtube.com/watch?v=72MufjAB5VM",
                embedUrl: "https://www.youtube.com/embed/72MufjAB5VM",
                publisher: {
                  "@type": "Organization",
                  name: "Mii Global Partner Holdings Sdn. Bhd.",
                  logo: {
                    "@type": "ImageObject",
                    url: "https://customer-assets.emergentagent.com/job_staymii-telegram-bot/artifacts/dbwmuy0k_A9358B2D-0233-4058-88E9-3B869B36D815.png",
                  },
                },
              }),
            }}
          />
        </motion.div>

        {/* Diagram: Email → Telegram Group → Team */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6"
        >
          <div className="bg-white border border-gray-200 rounded-2xl px-6 py-4 flex items-center gap-3 shadow-sm">
            <Mail className="w-5 h-5 text-red-500" />
            <span className="font-outfit font-semibold text-sm text-gray-900">Email Arrives</span>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-300 rotate-90 sm:rotate-0" />
          <div className="bg-[#0088CC]/5 border border-[#0088CC]/20 rounded-2xl px-6 py-4 flex items-center gap-3 shadow-sm">
            <TelegramIcon className="w-5 h-5 text-[#0088CC]" />
            <span className="font-outfit font-semibold text-sm text-gray-900">Telegram Group</span>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-300 rotate-90 sm:rotate-0" />
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl px-6 py-4 flex items-center gap-3 shadow-sm">
            <Send className="w-5 h-5 text-emerald-600" />
            <span className="font-outfit font-semibold text-sm text-gray-900">Team Responds</span>
          </div>
        </motion.div>
        <div className="text-center mt-12">
          <a href={BOT_LINK} target="_blank" rel="noopener noreferrer" onClick={() => trackCTAClick('how_it_works_cta')}
            className="inline-flex items-center gap-2 bg-[#0088CC] text-white font-manrope font-semibold px-8 py-3.5 rounded-full hover:bg-[#006699] transition-all hover:-translate-y-0.5 shadow-lg"
            data-testid="how-it-works-cta"
          >
            Get Instant Email <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

// ─── PAIN POINTS vs SOLUTION ───
function PainVsSolution() {
  const items = [
    { pain: "Missed emails", solution: "Instant Telegram alerts" },
    { pain: "Leads unattended", solution: "Sent to Telegram group for immediate action" },
    { pain: "Emails scattered", solution: "Centralized in one group" },
    { pain: "Team unaware", solution: "Everyone notified instantly" },
    { pain: "Manual inbox checking", solution: "No need to open email" },
    { pain: "Slow support", solution: "Real-time group response" },
    { pain: "Spam issues", solution: "Use secondary email" },
    { pain: "Slow OTP access", solution: "Instant delivery" },
  ];

  return (
    <section className="py-20 sm:py-28 px-4" data-testid="pain-solution-section">
      <div className="max-w-5xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
          <motion.p variants={fadeUp} className="font-manrope text-sm uppercase tracking-[0.2em] font-semibold text-gray-400 mb-3">Problems We Solve</motion.p>
          <motion.h2 variants={fadeUp} className="font-outfit font-bold text-3xl sm:text-4xl tracking-tight text-gray-900 mb-4">Stop Losing Leads. Start Responding Instantly.</motion.h2>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="space-y-3">
          {items.map((item, i) => (
            <motion.div key={i} variants={fadeUp}
              className="grid grid-cols-2 gap-4 sm:gap-8"
              data-testid={`pain-solution-${i}`}
            >
              <div className="bg-red-50 border border-red-100 rounded-xl px-5 py-4 flex items-center gap-3">
                <X className="w-4 h-4 text-red-500 flex-shrink-0" />
                <span className="font-manrope text-sm text-red-700">{item.pain}</span>
              </div>
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-5 py-4 flex items-center gap-3">
                <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                <span className="font-manrope text-sm text-emerald-800">{item.solution}</span>
              </div>
            </motion.div>
          ))}
        </motion.div>
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mt-10">
          <p className="font-outfit font-bold text-lg text-gray-900 mb-6">Faster response = more deals</p>
          <a href={BOT_LINK} target="_blank" rel="noopener noreferrer" onClick={() => trackCTAClick('pain_cta')}
            className="inline-flex items-center gap-2 bg-[#0088CC] text-white font-manrope font-semibold px-8 py-3.5 rounded-full hover:bg-[#006699] transition-all hover:-translate-y-0.5 shadow-lg"
            data-testid="pain-solution-cta"
          >
            Start Free Now <ArrowRight className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}

// ─── BEFORE vs AFTER ───
function BeforeAfter() {
  const beforeItems = ["Manual checking", "Slow replies", "Missed leads", "Scattered communication", "Poor team coordination"];
  const afterItems = ["Instant Telegram alerts", "Immediate responses", "All leads captured", "Centralized communication", "Team aligned in real time"];

  return (
    <section className="py-20 sm:py-28 px-4 bg-[#F8F9FA]" data-testid="before-after-section">
      <div className="max-w-5xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
          <motion.p variants={fadeUp} className="font-manrope text-sm uppercase tracking-[0.2em] font-semibold text-gray-400 mb-3">Transformation</motion.p>
          <motion.h2 variants={fadeUp} className="font-outfit font-bold text-3xl sm:text-4xl tracking-tight text-gray-900 mb-4">From Missed Opportunities to Instant Action</motion.h2>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
          className="grid md:grid-cols-2 gap-8"
        >
          {/* Before */}
          <motion.div variants={fadeUp} className="bg-white border border-gray-200 rounded-2xl p-8">
            <h3 className="font-outfit font-bold text-xl text-gray-400 mb-6 flex items-center gap-2">
              <X className="w-5 h-5 text-red-400" /> Before
            </h3>
            <ul className="space-y-4">
              {beforeItems.map((item, i) => (
                <li key={i} className="flex items-center gap-3 font-manrope text-sm text-gray-500">
                  <div className="w-2 h-2 rounded-full bg-red-300 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
          {/* After */}
          <motion.div variants={fadeUp} className="bg-[#0088CC] rounded-2xl p-8 text-white">
            <h3 className="font-outfit font-bold text-xl mb-6 flex items-center gap-2">
              <Check className="w-5 h-5 text-emerald-300" /> After
            </h3>
            <ul className="space-y-4">
              {afterItems.map((item, i) => (
                <li key={i} className="flex items-center gap-3 font-manrope text-sm text-white/90">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
        </motion.div>
        <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          className="text-center mt-10 font-outfit font-bold text-lg text-[#0088CC]"
        >
          Faster response = More conversions
        </motion.p>
      </div>
    </section>
  );
}

// ─── ROI / BUSINESS IMPACT ───
function ROISection() {
  return (
    <section className="py-20 sm:py-28 px-4" data-testid="roi-section">
      <div className="max-w-5xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-10">
          <motion.p variants={fadeUp} className="font-manrope text-sm uppercase tracking-[0.2em] font-semibold text-gray-400 mb-3">Business Impact</motion.p>
          <motion.h2 variants={fadeUp} className="font-outfit font-bold text-3xl sm:text-4xl tracking-tight text-gray-900 mb-4">How TelegramEmailBot Helps You Make More Money</motion.h2>
          <motion.p variants={fadeUp} className="font-manrope text-base text-gray-600 max-w-2xl mx-auto">
            Faster response. Every lead captured. More deals closed. Fewer missed opportunities.
          </motion.p>
        </motion.div>

        {/* Infographic */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-10"
        >
          <img
            src="/logos/make-more-money.png"
            alt="How TelegramEmailBot helps you make more money — Faster Response, Capture Every Lead, Team Efficiency, Close More Deals, Reduce Missed Opportunities. Just $14.99/year."
            className="w-full h-auto rounded-3xl shadow-[0_20px_60px_-24px_rgba(0,136,204,0.28)] border border-gray-100"
            loading="lazy"
            data-testid="roi-infographic"
          />
        </motion.div>

        {/* CTA below infographic */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <a
            href={BOT_LINK}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCTAClick('roi_cta')}
            className="inline-flex items-center gap-2 bg-[#0088CC] text-white font-manrope font-bold px-8 py-3.5 rounded-full hover:bg-[#006699] transition-all hover:-translate-y-0.5 shadow-lg"
            data-testid="roi-cta"
          >
            Get Your Inbox for $4.99 <ArrowRight className="w-4 h-4" />
          </a>
          <p className="font-manrope text-xs text-gray-500 mt-3">Less than the cost of 1 missed opportunity.</p>
        </motion.div>
      </div>
    </section>
  );
}

// ─── PROMO BANNER ───
function PromoBanner() {
  return (
    <section className="py-16 sm:py-20 px-4" data-testid="promo-banner-section">
      <div className="max-w-3xl mx-auto">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
          <a href={BOT_LINK} target="_blank" rel="noopener noreferrer" onClick={() => trackCTAClick('promo_banner_click')} className="block group">
            <img
              src="https://customer-assets.emergentagent.com/job_staymii-telegram-bot/artifacts/xmmxfa1a_telegram%20email%20bot%20promotion%20pricing.png"
              alt="Get your permanent @telegramemailbot.com email address for a one-time $4.99. 20 complimentary emails included."
              className="w-full rounded-2xl shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-[1.01]"
              loading="lazy"
            />
          </a>
        </motion.div>
      </div>
    </section>
  );
}

// ─── WHY CHOOSE US ───
function WhyChooseUs() {
  const reasons = [
    { icon: <Zap className="w-5 h-5" />, title: "Instant Setup", desc: "Pay once and your email is live in seconds. No forms, no verification." },
    { icon: <Shield className="w-5 h-5" />, title: "No Subscription", desc: "Just a one-time $4.99 for your permanent inbox. No recurring charges." },
    { icon: <Users className="w-5 h-5" />, title: "Works with Telegram Groups", desc: "Route emails to your team group for instant collaboration." },
    { icon: <Target className="w-5 h-5" />, title: "Never Miss a Lead", desc: "Instant notifications ensure every inquiry gets a response." },
    { icon: <MessageSquare className="w-5 h-5" />, title: "Centralized Communication", desc: "All emails in one place — your Telegram inbox." },
    { icon: <Eye className="w-5 h-5" />, title: "Privacy-First", desc: "No email scanning, no ads, no tracking. Your data stays private." },
  ];
  return (
    <section className="py-20 sm:py-28 px-4 bg-[#F8F9FA]" data-testid="why-choose-section">
      <div className="max-w-7xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
          <motion.p variants={fadeUp} className="font-manrope text-sm uppercase tracking-[0.2em] font-semibold text-gray-400 mb-3">Why Choose Us</motion.p>
          <motion.h2 variants={fadeUp} className="font-outfit font-bold text-3xl sm:text-4xl tracking-tight text-gray-900 mb-4">Built for Speed, Privacy, and Teamwork</motion.h2>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {reasons.map((r, i) => (
            <motion.div key={i} variants={fadeUp} className="flex gap-4 items-start" data-testid={`why-${i}`}>
              <div className="w-10 h-10 bg-white border border-gray-200 rounded-xl flex items-center justify-center flex-shrink-0 text-[#0088CC]">{r.icon}</div>
              <div>
                <h3 className="font-outfit font-bold text-base text-gray-900 mb-1">{r.title}</h3>
                <p className="font-manrope text-sm text-gray-500 leading-relaxed">{r.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ─── PRICING ───
function Pricing() {
  const tiers = [
    {
      key: "starter",
      badge: "One-Time",
      badgeColor: "bg-emerald-500",
      accent: "border-emerald-500 ring-2 ring-emerald-100",
      icon: <Mail className="w-6 h-6" />,
      iconBg: "bg-emerald-50 text-emerald-600",
      title: "Starter Email",
      price: "$4.99",
      priceSuffix: "one-time",
      tagline: "Own your permanent @telegramemailbot.com email address.",
      features: [
        "1 Permanent Email Address",
        "Instant Telegram Notifications",
        "20 Complimentary Incoming Emails",
        "Basic Attachments",
        "Secure Cloud Hosting",
      ],
      footnote: "Email address changes not included ($2.99 fee if you change later).",
      cta: "Get Starter — $4.99",
    },
    {
      key: "yearly",
      badge: "Most Popular",
      badgeColor: "bg-[#0088CC]",
      accent: "border-[#0088CC] ring-2 ring-[#0088CC]/10 lg:scale-[1.03]",
      icon: <Sparkles className="w-6 h-6" />,
      iconBg: "bg-[#0088CC]/10 text-[#0088CC]",
      title: "Inbox Storage & AI",
      price: "$14.99",
      priceSuffix: "per year",
      tagline: "After your first 20 emails — unlimited receiving + AI + priority.",
      features: [
        "Continue Receiving Unlimited Emails",
        "Unlimited Inbox Storage",
        "AI Email Assistant",
        "AI Email Summaries",
        "Priority Email Processing",
        "Access to Future Premium Features",
      ],
      footnote: "Requires an active Starter Email to subscribe. Fair-use policy applies.",
      cta: "Subscribe — $14.99/yr",
    },
    {
      key: "change",
      badge: "Add-on",
      badgeColor: "bg-gray-500",
      accent: "border-gray-200",
      icon: <RotateCcw className="w-6 h-6" />,
      iconBg: "bg-gray-100 text-gray-700",
      title: "Email Address Change",
      price: "$2.99",
      priceSuffix: "one-time",
      tagline: "Change your @telegramemailbot.com address to a new one anytime.",
      features: [
        "Fresh permanent email address",
        "Old address stops receiving",
        "Complimentary email count resets to 0",
        "No subscription required",
      ],
      footnote: "Use /reset in the Telegram bot to trigger the change.",
      cta: "Change Address — $2.99",
    },
  ];

  return (
    <section id="pricing" className="py-20 sm:py-28 px-4" data-testid="pricing-section">
      <div className="max-w-6xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
          <motion.p variants={fadeUp} className="font-manrope text-sm uppercase tracking-[0.2em] font-semibold text-gray-400 mb-3">Pricing</motion.p>
          <motion.h2 variants={fadeUp} className="font-outfit font-bold text-3xl sm:text-4xl tracking-tight text-gray-900 mb-4">Simple. Fair. Pay Only for What You Need.</motion.h2>
          <motion.p variants={fadeUp} className="font-manrope text-base text-gray-500 max-w-xl mx-auto">Own your inbox for a one-time $4.99. Subscribe only when you actually need unlimited emails and AI. No hidden fees, no surprise renewals for the starter tier.</motion.p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {tiers.map((t, i) => (
            <motion.div
              key={t.key}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className={`relative bg-white rounded-2xl border-2 ${t.accent} p-7 sm:p-8 flex flex-col`}
              data-testid={`pricing-card-${t.key}`}
            >
              <div className={`absolute -top-3 left-1/2 -translate-x-1/2 ${t.badgeColor} text-white font-manrope font-semibold text-[11px] uppercase tracking-wider px-4 py-1 rounded-full shadow-md`}>
                {t.badge}
              </div>
              <div className={`w-12 h-12 rounded-xl ${t.iconBg} flex items-center justify-center mb-4`}>{t.icon}</div>
              <h3 className="font-outfit font-bold text-xl text-gray-900 mb-1">{t.title}</h3>
              <p className="font-manrope text-sm text-gray-500 mb-5 leading-relaxed min-h-[40px]">{t.tagline}</p>
              <div className="flex items-baseline gap-1.5 mb-6">
                <span className="font-outfit font-black text-4xl text-gray-900">{t.price}</span>
                <span className="font-manrope text-sm text-gray-500">{t.priceSuffix}</span>
              </div>
              <ul className="space-y-2.5 mb-6 flex-grow">
                {t.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2 font-manrope text-sm text-gray-700">
                    <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <p className="font-manrope text-xs text-gray-400 italic mb-5 leading-relaxed">{t.footnote}</p>
              <a
                href={BOT_LINK}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackCTAClick(`pricing_${t.key}`)}
                className={`block text-center font-manrope font-bold px-6 py-3 rounded-full transition-all hover:-translate-y-0.5 shadow-md ${
                  t.key === "yearly"
                    ? "bg-[#0088CC] text-white hover:bg-[#006699]"
                    : t.key === "starter"
                      ? "bg-emerald-500 text-white hover:bg-emerald-600"
                      : "bg-gray-900 text-white hover:bg-black"
                }`}
                data-testid={`pricing-cta-${t.key}`}
              >
                {t.cta}
              </a>
            </motion.div>
          ))}
        </div>

        {/* Money-back guarantee — risk reversal */}
        <div className="mt-10 max-w-2xl mx-auto" data-testid="pricing-guarantee">
          <div className="flex items-center gap-4 bg-emerald-50 border border-emerald-200 rounded-2xl px-6 py-5">
            <div className="w-12 h-12 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
              <Shield className="w-6 h-6" strokeWidth={2.5} />
            </div>
            <div>
              <p className="font-outfit font-bold text-gray-900 text-base">7-Day Money-Back Guarantee</p>
              <p className="font-manrope text-sm text-gray-600 leading-relaxed">Not happy in your first 7 days? Email us and we'll refund your $4.99 in full — no questions asked. Zero risk to try.</p>
            </div>
          </div>
        </div>

        {/* Trust badges under pricing */}
        <div className="flex justify-center mt-10">
          <TrustBadges variant="light" />
        </div>

        {/* How it works */}
        <div className="mt-16 max-w-3xl mx-auto bg-gray-50 border border-gray-100 rounded-2xl p-8">
          <h3 className="font-outfit font-bold text-lg text-gray-900 mb-5 text-center">How It Works</h3>
          <ol className="space-y-3 font-manrope text-sm text-gray-700">
            <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-emerald-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span><span>Purchase your permanent <b>@telegramemailbot.com</b> email for a one-time <b>$4.99</b>.</span></li>
            <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-emerald-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span><span>Receive your first <b>20 complimentary emails</b> at no extra cost.</span></li>
            <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#0088CC] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span><span>After email #20, subscribe to <b>Inbox Storage &amp; AI</b> for <b>$14.99/year</b> to keep receiving with AI + unlimited storage.</span></li>
            <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-gray-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">4</span><span>Want to change your address later? <b>$2.99</b> one-time via <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">/reset</code> in Telegram.</span></li>
          </ol>
        </div>
      </div>
    </section>
  );
}

// ─── FAQ ───
function FAQ() {
  const [open, setOpen] = useState(null);
  const faqs = [
    { q: "Do I need to sign up or register?", a: "No registration, no account, no password. You just open the Telegram bot and pay a one-time $4.99 — your permanent email address is revealed instantly." },
    { q: "Is it really free?", a: "No — but it's affordable. You pay a one-time $4.99 to get your permanent email address plus 20 complimentary incoming emails. After that, subscribe to Inbox Storage & AI for $14.99/year to keep receiving unlimited emails and unlock AI features." },
    { q: "Do I need a credit card to start?", a: "You'll need a card to purchase the Starter Email ($4.99 one-time). We use Stripe for secure checkout — we never see or store your card number." },
    { q: "Can I use this for my team or business?", a: "Absolutely. Add the bot to a Telegram group and everyone in the group receives customer emails. It's like a shared inbox but inside Telegram." },
    { q: "How fast do emails arrive?", a: "Emails typically arrive within 30-60 seconds of being sent. The system polls for new emails every 30 seconds and delivers them as Telegram messages instantly." },
    { q: "Can I send emails, not just receive?", a: "Yes! Use the /send command to compose and send emails. You can also reply directly to any received email — just reply to the message in Telegram." },
    { q: "Does it support attachments?", a: "Yes. PDF files, images, documents, and other attachments are delivered directly to your Telegram chat." },
    { q: "Is my email secure and private?", a: "Yes. We don't scan your emails for advertising. There are no ads. Your emails are forwarded directly to your Telegram chat and are not stored or analyzed for marketing purposes." },
    { q: "Can I have multiple email addresses?", a: "Each private chat and each Telegram group gets its own unique email address. You can change your address anytime with /reset ($2.99 one-time fee)." },
    { q: "What happens after my first 20 emails?", a: "Your email delivery pauses. Subscribe to Inbox Storage & AI for $14.99/year to unlock unlimited emails, unlimited inbox storage, AI email summaries, priority processing, and access to future premium features." },
    { q: "What email domain do I get?", a: "You receive a unique @telegramemailbot.com email address. The username is randomly generated for privacy, but you can change it anytime with the /reset command ($2.99)." },
    { q: "Can I block spam senders?", a: "Yes! Use the /block command followed by the sender's email address to block them. You can view your blocked list with /blocklist and unblock with /unblock." },
    { q: "Can I cancel my subscription?", a: "Yes. You can cancel your annual Inbox Storage & AI subscription at any time. Your access continues until the end of the paid period." },
    { q: "Will I lose my email address if I don't upgrade?", a: "Your address keeps working for your 20 complimentary emails. After that, new emails are held and you have 14 days to upgrade to Inbox Storage & AI ($14.99/year). If you don't, the address and held emails are deleted. We remind you on day 0, 7 and 13." },
    { q: "Is payment secure?", a: "Yes. Payments are securely processed by Stripe, a PCI Level 1 certified payment provider. We never see or store your card number." },
    { q: "Why not just use a free temporary email instead?", a: "Free temp-mail addresses expire in minutes, get flooded with spam, are often blocked by services, and you can't receive alerts in Telegram or share them with your team. Your $4.99 address is permanent, private, spam-controlled, delivers straight to Telegram (and groups), and is yours forever — a one-time cost, not a gamble." },
    { q: "What if it doesn't work for me — can I get a refund?", a: "Yes. We offer a 7-day money-back guarantee. If you're not satisfied within 7 days of your $4.99 Starter purchase, just email us and we'll refund you in full — no questions asked." },
    { q: "Why do you charge for an email address?", a: "The one-time $4.99 fee prevents abuse, spam registrations and disposable email usage. It also lets us keep the platform reliable, invest in secure cloud infrastructure and continuously improve AI features." },
    { q: "Do you read my emails?", a: "No. Emails are only processed to deliver notifications and (for subscribers) generate AI summaries. We never scan or sell your emails, and we never use them for advertising or marketing." },
  ];
  return (
    <section id="faq" className="py-20 sm:py-28 px-4 bg-[#F8F9FA]" data-testid="faq-section">
      <div className="max-w-3xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
          <motion.p variants={fadeUp} className="font-manrope text-sm uppercase tracking-[0.2em] font-semibold text-gray-400 mb-3">FAQ</motion.p>
          <motion.h2 variants={fadeUp} className="font-outfit font-bold text-3xl sm:text-4xl tracking-tight text-gray-900 mb-4">Frequently Asked Questions</motion.h2>
        </motion.div>
        <div className="space-y-2">
          {faqs.map((faq, i) => (
            <div key={i} className="border-b border-gray-200" data-testid={`faq-${i}`}>
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center justify-between py-5 text-left font-outfit font-semibold text-base text-gray-900 hover:text-[#0088CC] transition-colors"
              >
                {faq.q}
                {open === i ? <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />}
              </button>
              {open === i && (
                <div className="pb-5 font-manrope text-sm text-gray-500 leading-relaxed">{faq.a}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── BLOG ───
function BlogSection() {
  return (
    <section className="py-20 sm:py-28 px-4" data-testid="blog-section">
      <div className="max-w-7xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
          <motion.p variants={fadeUp} className="font-manrope text-sm uppercase tracking-[0.2em] font-semibold text-gray-400 mb-3">Blog</motion.p>
          <motion.h2 variants={fadeUp} className="font-outfit font-bold text-3xl sm:text-4xl tracking-tight text-gray-900 mb-4">From the Blog</motion.h2>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {blogPosts.slice(0, 3).map((post, i) => (
            <motion.div key={i} variants={fadeUp}>
              <Link to={`/blog/${post.slug}`} className="block bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-md hover:border-gray-300 transition-all group" data-testid={`blog-card-${i}`}>
                <div className="font-manrope text-xs text-gray-400 mb-3">{post.date} &middot; {post.readTime}</div>
                <h3 className="font-outfit font-bold text-base text-gray-900 mb-2 group-hover:text-[#0088CC] transition-colors">{post.title}</h3>
                <p className="font-manrope text-sm text-gray-500 leading-relaxed line-clamp-3">{post.description}</p>
              </Link>
            </motion.div>
          ))}
        </motion.div>
        <div className="text-center mt-10">
          <Link to="/blog" className="inline-flex items-center gap-2 font-manrope font-semibold text-sm text-[#0088CC] hover:text-[#006699] transition-colors" data-testid="blog-view-all">
            View All Articles <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ─── GUIDES SECTION ───
function GuidesSection() {
  const guides = [
    { title: "Telegram Email Bot", desc: "Get your own email address inside Telegram for a one-time $4.99. Full guide.", slug: "telegram-email-bot" },
    { title: "Receive Email on Telegram", desc: "Forward any email to Telegram. Step-by-step setup.", slug: "receive-email-on-telegram" },
    { title: "Telegram Email Integration", desc: "Connect email to Telegram for teams. No coding needed.", slug: "telegram-email-integration" },
    { title: "Temporary Email in Telegram", desc: "Private, instant disposable email without expiration.", slug: "temporary-email-telegram" },
    { title: "Email to Telegram Alerts", desc: "Turn any email into instant Telegram notifications.", slug: "email-to-telegram-alerts" },
    { title: "Create an Email Bot in Telegram", desc: "No code, no BotFather. A working email bot in 30 seconds.", slug: "create-email-bot-telegram" },
    { title: "Etlgr Bot Alternative", desc: "Pay once, no ads in your emails. Side-by-side comparison.", slug: "etlgr-bot-alternative" },
    { title: "Gmail in Telegram", desc: "Forward Gmail to Telegram safely, or get a new address.", slug: "gmail-in-telegram" },
  ];

  return (
    <section className="py-20 sm:py-28 px-4 bg-[#F8F9FA]" data-testid="guides-section">
      <div className="max-w-7xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
          <motion.p variants={fadeUp} className="font-manrope text-sm uppercase tracking-[0.2em] font-semibold text-gray-400 mb-3">Guides & Solutions</motion.p>
          <motion.h2 variants={fadeUp} className="font-outfit font-bold text-3xl sm:text-4xl tracking-tight text-gray-900 mb-4">Learn How to Use Email in Telegram</motion.h2>
          <motion.p variants={fadeUp} className="font-manrope text-base text-gray-500 max-w-xl mx-auto">In-depth guides covering everything from setup to advanced team workflows.</motion.p>
        </motion.div>

        {/* Featured Partner: StayMii HR */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-8">
          <Link to="/staymii-hr-job-application-form" className="block bg-gradient-to-r from-emerald-50 to-sky-50 border border-emerald-200 rounded-2xl p-6 sm:p-8 hover:shadow-lg hover:border-emerald-300 transition-all group" data-testid="guide-featured-staymii">
            <div className="sm:flex sm:items-center sm:justify-between sm:gap-6">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 font-manrope text-xs font-semibold px-3 py-1 rounded-full mb-3">
                  <Briefcase className="w-3 h-3" /> Featured Partner Solution
                </div>
                <h3 className="font-outfit font-bold text-xl text-gray-900 mb-2 group-hover:text-emerald-700 transition-colors">Free Job Application Form + Instant Telegram Alerts</h3>
                <p className="font-manrope text-sm text-gray-500 leading-relaxed max-w-xl">StayMii HR gives you a free job application form. TelegramEmailBot notifies your HR team instantly when someone applies. Centralize all applicants from every job portal.</p>
              </div>
              <div className="mt-4 sm:mt-0 flex-shrink-0">
                <span className="inline-flex items-center gap-2 bg-emerald-600 text-white font-manrope font-bold px-6 py-3 rounded-full text-sm group-hover:bg-emerald-700 transition-all">
                  Learn More <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </div>
          </Link>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {guides.map((guide, i) => (
            <motion.div key={i} variants={fadeUp}>
              <Link to={`/${guide.slug}`} className="block bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-md hover:border-[#0088CC]/30 transition-all group h-full" data-testid={`guide-card-${i}`}>
                <div className="w-9 h-9 bg-[#0088CC]/10 rounded-lg flex items-center justify-center mb-4">
                  <Mail className="w-4 h-4 text-[#0088CC]" />
                </div>
                <h3 className="font-outfit font-bold text-base text-gray-900 mb-2 group-hover:text-[#0088CC] transition-colors">{guide.title}</h3>
                <p className="font-manrope text-sm text-gray-500 leading-relaxed">{guide.desc}</p>
                <span className="inline-flex items-center gap-1 mt-3 font-manrope text-xs font-semibold text-[#0088CC]">Read guide <ArrowRight className="w-3 h-3" /></span>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ─── CTA WITH QR ───
function CTASection() {
  return (
    <section className="py-20 sm:py-28 px-4 bg-[#0A0A0A]" data-testid="cta-section">
      <div className="max-w-5xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left text */}
            <div className="text-center lg:text-left order-2 lg:order-1">
              <motion.h2 variants={fadeUp} className="font-outfit font-bold text-3xl sm:text-4xl text-white mb-4">
                Get Your Email in 5 Seconds
              </motion.h2>
              <motion.p variants={fadeUp} className="font-manrope text-base text-gray-400 mb-3 max-w-md mx-auto lg:mx-0">
                Scan the QR code or tap the button below to open <strong className="text-white">@instant_email_bot</strong> in Telegram. Own your permanent inbox for $4.99.
              </motion.p>
              <motion.p variants={fadeUp} className="font-manrope text-sm text-gray-500 mb-8 max-w-md mx-auto lg:mx-0">
                One-time $4.99. 20 free emails included. No renewals until you want unlimited.
              </motion.p>
              <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-manrope font-semibold text-xs px-4 py-2 rounded-full mb-6" data-testid="cta-guarantee">
                <Shield className="w-4 h-4" strokeWidth={2.5} /> 7-day money-back guarantee · Secure Stripe checkout
              </motion.div>
              <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                <a
                  href={BOT_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackCTAClick('cta_open_telegram')}
                  className="inline-flex items-center justify-center gap-2.5 bg-[#0088CC] text-white font-manrope font-bold px-8 py-4 rounded-full hover:bg-[#006699] transition-all hover:-translate-y-0.5 shadow-lg text-base"
                  data-testid="cta-open-telegram-button"
                >
                  <TelegramIcon className="w-5 h-5" />
                  Open in Telegram
                </a>
                <a
                  href={CHANNEL_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 border border-gray-700 text-gray-300 font-manrope font-semibold px-8 py-4 rounded-full hover:border-gray-500 hover:text-white transition-all"
                  data-testid="cta-channel-button"
                >
                  Join Telegram Channel
                </a>
              </motion.div>
            </div>
            {/* Right QR */}
            <motion.div variants={fadeUp} className="flex justify-center order-1 lg:order-2">
              <a
                href={BOT_LINK}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackCTAClick('qr_code_click')}
                className="group"
                data-testid="cta-qr-link"
              >
                <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-[0_8px_40px_rgba(0,136,204,0.15)] hover:shadow-[0_12px_50px_rgba(0,136,204,0.25)] transition-all duration-300 group-hover:scale-[1.02]">
                  <div className="relative">
                    <img
                      src={QR_CODE_URL}
                      alt="Scan to open @instant_email_bot on Telegram"
                      className="w-52 h-52 sm:w-60 sm:h-60 rounded-xl mx-auto"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 rounded-xl border-2 border-[#0088CC]/30 animate-qr-pulse pointer-events-none" />
                  </div>
                  <p className="font-manrope text-sm font-semibold text-gray-700 text-center mt-4">Scan to open in Telegram</p>
                  <p className="font-manrope text-xs text-gray-400 text-center mt-1">@instant_email_bot</p>
                </div>
              </a>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ─── FOOTER ───
function Footer() {
  return (
    <footer className="bg-[#0A0A0A] text-gray-300 pt-20 pb-12 px-4" data-testid="footer">
      <div className="max-w-7xl mx-auto">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img src={LOGO_URL} alt="TelegramEmailBot" className="w-5 h-5 rounded object-cover" />
              <span className="font-outfit font-bold text-white">TelegramEmailBot</span>
            </div>
            <p className="font-manrope text-sm leading-relaxed text-gray-300">Receive emails instantly in Telegram or your team group. No signup, no subscription — just a one-time $4.99.</p>
          </div>
          <div>
            <h4 className="font-outfit font-bold text-white text-sm mb-4">Product</h4>
            <div className="space-y-2 font-manrope text-sm text-gray-300">
              <a href="#use-cases" className="block hover:text-white transition-colors">Use Cases</a>
              <a href="#how-it-works" className="block hover:text-white transition-colors">How It Works</a>
              <a href="#pricing" className="block hover:text-white transition-colors">Pricing</a>
              <a href="#faq" className="block hover:text-white transition-colors">FAQ</a>
            </div>
          </div>
          <div>
            <h4 className="font-outfit font-bold text-white text-sm mb-4">Resources</h4>
            <div className="space-y-2 font-manrope text-sm text-gray-300">
              <Link to="/blog" className="block hover:text-white transition-colors">Blog</Link>
              <Link to="/telegram-email-bot" className="block hover:text-white transition-colors">Telegram Email Bot</Link>
              <Link to="/receive-email-on-telegram" className="block hover:text-white transition-colors">Receive Email on Telegram</Link>
              <Link to="/telegram-email-integration" className="block hover:text-white transition-colors">Email Integration</Link>
              <Link to="/temporary-email-telegram" className="block hover:text-white transition-colors">Temporary Email</Link>
              <Link to="/email-to-telegram-alerts" className="block hover:text-white transition-colors">Email to Telegram Alerts</Link>
              <Link to="/create-email-bot-telegram" className="block hover:text-white transition-colors" data-testid="footer-link-create-bot">Create an Email Bot</Link>
              <Link to="/etlgr-bot-alternative" className="block hover:text-white transition-colors" data-testid="footer-link-etlgr">Etlgr Alternative</Link>
              <Link to="/gmail-in-telegram" className="block hover:text-white transition-colors" data-testid="footer-link-gmail">Gmail in Telegram</Link>
              <Link to="/staymii-hr-job-application-form" className="block hover:text-white transition-colors">Free Job Application Form</Link>
              <a href={BOT_LINK} target="_blank" rel="noopener noreferrer" className="block hover:text-white transition-colors">Open Bot</a>
              <a href={CHANNEL_LINK} target="_blank" rel="noopener noreferrer" className="block hover:text-white transition-colors">Support</a>
            </div>
          </div>
          <div>
            <h4 className="font-outfit font-bold text-white text-sm mb-4">Company</h4>
            <div className="space-y-2 font-manrope text-sm text-gray-300">
              <Link to="/about" className="block hover:text-white transition-colors" data-testid="footer-about-link">About Us</Link>
              <Link to="/contact" className="block hover:text-white transition-colors" data-testid="footer-contact-link">Contact</Link>
              <Link to="/privacy" className="block hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
              <Link to="/terms" className="block hover:text-white transition-colors" data-testid="footer-terms-link">Terms of Service</Link>
            </div>
          </div>
        </div>
        {/* Large branding */}
        <div className="border-t border-gray-700/70 pt-12 mb-8">
          <p className="font-outfit font-black text-[6vw] sm:text-[4vw] lg:text-[3vw] text-gray-700 text-center leading-none select-none">TelegramEmailBot</p>
        </div>

        {/* Trust & company info */}
        <div className="border-t border-gray-700/70 pt-6 pb-6 mb-2">
          <p className="font-manrope text-xs text-gray-300 text-center max-w-2xl mx-auto leading-relaxed" data-testid="footer-trust-note">
            <span className="inline-flex items-center gap-1.5 text-emerald-400 font-semibold">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
              Secure
            </span>
            {" — "}TelegramEmailBot does <strong className="text-white">not store email passwords</strong> and only processes notifications authorized by users. All traffic is encrypted via HTTPS.
          </p>
        </div>

        <div className="font-manrope text-xs text-gray-300 flex flex-col sm:flex-row justify-between items-center gap-2">
          <span>&copy; 2026 TelegramEmailBot. All rights reserved.</span>
          <span className="text-center sm:text-right" data-testid="footer-company-name">
            Operated by <strong className="text-white">Mii Global Partner Holdings Sdn. Bhd.</strong> · Reg. No. 202601028203 (1690298-K) · Penang, Malaysia
          </span>
        </div>
      </div>
    </footer>
  );
}

// ─── FAQ SCHEMA (SEO) ───
function FAQSchema() {
  const faqs = [
    { q: "Do I need to sign up or register?", a: "No. Open the Telegram bot, pay a one-time $4.99, and your email address is revealed instantly." },
    { q: "Is the Instant Email Bot free?", a: "No, but affordable. One-time $4.99 gets you a permanent @telegramemailbot.com email address plus 20 free incoming emails. Subscribe to Inbox Storage & AI for $14.99/year for unlimited emails." },
    { q: "Can I use this for my business or team?", a: "Yes. Add the bot to a Telegram group and everyone receives customer emails." },
    { q: "How fast do emails arrive in Telegram?", a: "Emails arrive within 30-60 seconds." },
    { q: "Can I send emails from Telegram?", a: "Yes. Use /send or reply to any received email." },
    { q: "Can I block spam senders?", a: "Yes. Use the /block command to block senders. Use /blocklist to see your blocked list." },
  ];
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />;
}

// ─── PAGE ───
export default function LandingPage() {
  useEffect(() => {
    const href = 'https://telegramemailbot.com/';
    const link = document.querySelector('link[rel="canonical"]') || (() => { const l = document.createElement('link'); l.rel = 'canonical'; document.head.appendChild(l); return l; })();
    link.href = href;
    const og = document.querySelector('meta[property="og:url"]');
    if (og) og.setAttribute('content', href);
  }, []);
  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <FAQSchema />
      <Header />
      <Hero />
      <CompanyTrust />
      <WhatIs />
      <UseCases />
      <SeeItInAction />
      <HowItWorks />
      <PainVsSolution />
      <BeforeAfter />
      <ROISection />
      <PromoBanner />
      <WhyChooseUs />
      <LiveStats />
      <FounderStory />
      <Testimonials />
      <WhyWeCharge />
      <Pricing />
      <FAQ />
      <BlogSection />
      <GuidesSection />
      <CTASection />
      <TrustBanner />
      <Footer />
    </div>
  );
}
